import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Loads the active subscription plans from the dynamic catalog.
 *
 * Used by every plan dropdown (Create Tenant, Subscriptions Manage,
 * Tenant Detail plan switch). Returns:
 *
 *   { plans, loading, error, refresh }
 *
 * The endpoint is super-admin-only; non-admin callers will get a 403
 * and `plans` will stay [] silently. The dropdowns themselves should
 * therefore degrade gracefully (we already do — empty <select> just
 * shows "no plans").
 */
export default function useSubscriptionPlans({ activeOnly = true } = {}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = activeOnly ? "/admin/plans/active" : "/admin/plans";
      const { data } = await api.get(url);
      setPlans(data?.plans || []);
    } catch (e) {
      setError(e);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { plans, loading, error, refresh };
}
